import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { getCropFields, loadCropFieldsSuccess } from '@next/arable-lib/crop-field';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { SummaryCardAction, SummaryCardComponent, NotificationEmptyComponent } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, select, Store } from '@ngrx/store';
import { SeriesOptionsType } from 'highcharts';
import { Subject, Subscription } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import {
  EnvironmentalImpactReport,
  EnvironmentalImpactState,
  EnvironmentalImpactWithBenchmark,
} from '../../environmental-impact.models';
import { loadEnvironmentalImpactWithBenchmark } from '../../store/environmental-impact.actions';
import { getEnvironmentalImpactsWithBenchmarks } from '../../store/environmental-impact.selectors';
import { cropYearChanged } from '../../../../../store/actions';
import { selectCropYear } from '../../../../../store/selectors';
import { EnvironmentalImpactReportDialogComponent } from '../report/environmental-impact-report-dialog.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { ChartComponent } from '@next/core-lib/chart';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDivider } from '@angular/material/divider';
import { MatButton } from '@angular/material/button';
import { AsyncPipe, DecimalPipe } from '@angular/common';

@Component({
    selector: 'app-widget-environmental-impact-points',
    templateUrl: './environmental-impact-points.component.html',
    styleUrls: ['./environmental-impact-points.component.scss'],
    imports: [
        SummaryCardComponent,
        NotificationEmptyComponent,
        MatFormField,
        MatLabel,
        MatSelect,
        MatOption,
        ChartComponent,
        MatIcon,
        MatTooltip,
        MatDivider,
        MatButton,
        AsyncPipe,
        DecimalPipe,
        TranslatePipe,
    ],
})
export class EnvironmentalImpactPointsComponent implements OnInit, OnDestroy {
  private store = inject<Store<EnvironmentalImpactState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly actions = inject(ActionsSubject);
  private readonly destroy$ = new Subject<void>();

  cropFields$ = this.store.select(getCropFields);
  readonly cropYear = toSignal(this.store.select(selectCropYear), {
    initialValue: new Date().getFullYear(),
  });
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  companyName: string | undefined;
  getEnvironmentalImpactReports$!: Subscription;
  selectedCropFieldId: number | undefined;
  environmentalImpactsWithBenchmarks: EnvironmentalImpactWithBenchmark[] = [];
  selectedEnvironmentalImpactWithBenchmark: EnvironmentalImpactWithBenchmark | undefined;
  dataSource: SeriesOptionsType[] | undefined;
  chartOptions: Highcharts.Options | undefined;
  informationTooltipText = '';
  cardActions = [
    {
      id: 'info',
      icon: 'info_outline',
      callback: () => this.openInformationDialog(),
    },
  ] as SummaryCardAction[];

  ngOnInit() {
    // On first init, load all crop fields and select the first one. Then, fetch the
    // environmental impact report for the first cropfield found.
    // Use take(1) and setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    this.store
      .select(getCropFields)
      .pipe(
        filter((cropFields) => cropFields.length > 0),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((cropFields) => this.loadReportForCropFields(cropFields));

    this.actions
      .pipe(
        ofType(cropYearChanged),
        tap(() => this.clearSelectedReport()),
        switchMap(() =>
          this.actions.pipe(
            ofType(loadCropFieldsSuccess),
            take(1),
            map(({ payload }) => payload.cropFields),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((cropFields) => this.loadReportForCropFields(cropFields));

    // The highcharts bar chart needs to be rebuilt when the data changes. This is why
    // the subscription listens to store changes.
    this.getEnvironmentalImpactReports$ = this.store
      .select(getEnvironmentalImpactsWithBenchmarks)
      .subscribe((reports) => {
        this.environmentalImpactsWithBenchmarks = reports;
        const report = reports.find(
          (x) => x.environmentalImpactReport.cropFieldId === this.selectedCropFieldId,
        );
        if (!report) {
          this.selectedEnvironmentalImpactWithBenchmark = undefined;
          this.dataSource = undefined;
          this.chartOptions = undefined;
          this.informationTooltipText = '';
          return;
        }
        this.selectEnvironmentalImpactReport(report);
        this.cdr.markForCheck();
      });

    this.setCustomerCompanyName();
  }

  selectEnvironmentalImpactReport(report: EnvironmentalImpactWithBenchmark) {
    this.selectedEnvironmentalImpactWithBenchmark = report;

    const prepareScore = (score: number) => (score ? Math.round(score) : 0);

    this.dataSource = [
      {
        type: 'column',
        name: this.locale.translate('value'),
        color: '#B2DF8A',
        data: [
          prepareScore(report.environmentalImpactReport?.totals.aquaticLife),
          prepareScore(report.environmentalImpactReport?.totals.soilLife),
          prepareScore(report.environmentalImpactReport?.totals.groundwaterWashout),
        ],
      },
      {
        type: 'column',
        name: this.locale.translate('benchmark'),
        color: '#6B6B6B',
        data: [
          prepareScore(report.benchmark?.aquaticLifeMean),
          prepareScore(report.benchmark?.soilLifeMean),
          prepareScore(report.benchmark?.groundwaterWashoutMean),
        ],
      },
    ] as SeriesOptionsType[];

    this.chartOptions = {
      title: '',
      chart: {
        height: '250px',
      },
      xAxis: {
        categories: [
          this.locale.translate('environmentalImpactPointsAquaticLife'),
          this.locale.translate('environmentalImpactPointsSoilLife'),
          this.locale.translate('environmentalImpactPointsGroundWaterWashout'),
        ],
      },
      yAxis: {
        title: {
          text: null,
        },
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
      },
    } as Highcharts.Options;

    let tooltipMessage = '';
    if (report.environmentalImpactReport.organicMatterPercentage == null) {
      tooltipMessage += this.locale.translate(
        'environmentalImpactPointsTooltipNoCropFieldOrganicMatterPercentage',
      );
    }
    if (report.benchmark == null) {
      tooltipMessage += this.locale.translate(
        'environmentalImpactPointsTooltipBenchmarkUnavailable',
      );
    }

    this.informationTooltipText = tooltipMessage;
  }

  onCropFieldChanged() {
    const report = this.environmentalImpactsWithBenchmarks.find(
      (x) => x.environmentalImpactReport.cropFieldId === this.selectedCropFieldId,
    );
    if (report) {
      this.selectEnvironmentalImpactReport(report);
    } else if(this.selectedCropFieldId !== undefined) {
      this.store.dispatch(
        loadEnvironmentalImpactWithBenchmark({ cropFieldId: this.selectedCropFieldId }),
      );
    }

    this.ai.trackEvent('EnvironmentalImpactPoints.switchCropField', {});
  }

  openInformationDialog(): void {
    this.ai.trackEvent('EnvironmentalImpactPoints.openInformationDialog', {});
    this.dialog.notify(
      {
        title: this.locale.translate('environmentalImpactPointsInfoTitle'),
        text: this.locale.translate('environmentalImpactPointsInfoText'),
      },
      {
        maxWidth: '600px',
      },
    );
  }

  openEnvironmentalImpactReportDialog(): void {
    this.ai.trackEvent('EnvironmentalImpactPoints.openReportDialog', {});
    const report = this.getReportBySelectedCropField();

    if(!report){
      console.error('No report found for selected crop field', this.selectedCropFieldId);
      return;
    }

    this.dialog.form(
      EnvironmentalImpactReportDialogComponent,
      {
        report: report,
        title: `${this.companyName} - ${this.locale.translate('environmentalImpactReportTitle')} ${
          this.cropYear()
        }`,
        fullscreen: true,
        confirmButton: false,
        pushHistoryState: false,
        cancelButtonText: this.locale.translate('close'),
      },
      { maxWidth: '90vw', width: '100%' },
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.getEnvironmentalImpactReports$.unsubscribe();
  }

  private loadReportForCropFields(cropFields: { id: number }[]) {
    if (!cropFields.length) {
      this.clearSelectedReport();
      return;
    }

    // Defer the state update to the next change detection cycle
    setTimeout(() => {
      const selectedCropField = cropFields.find(({ id }) => id === this.selectedCropFieldId);
      this.selectedCropFieldId = selectedCropField?.id ?? cropFields[0].id;
      this.store.dispatch(
        loadEnvironmentalImpactWithBenchmark({ cropFieldId: this.selectedCropFieldId }),
      );
      this.cdr.markForCheck();
    });
  }

  private clearSelectedReport(): void {
    this.selectedCropFieldId = undefined;
    this.environmentalImpactsWithBenchmarks = [];
    this.selectedEnvironmentalImpactWithBenchmark = undefined;
    this.dataSource = undefined;
    this.chartOptions = undefined;
    this.informationTooltipText = '';
    this.cdr.markForCheck();
  }

  private setCustomerCompanyName() {
    this.customer$.subscribe((customer) => {
      if (!customer) {
        return;
      }
      this.companyName = customer.name;
    });
  }

  private getReportBySelectedCropField(): EnvironmentalImpactReport | undefined {
    return this.environmentalImpactsWithBenchmarks.find(
      (report) => report.environmentalImpactReport.cropFieldId === this.selectedCropFieldId,
    )?.environmentalImpactReport;
  }
}
